/* Módulo Foro de la consola del dueño: la cola de denuncias.
 *
 * Vive aparte del módulo de paneles porque son dos oficios distintos. Allá se
 * decide quién entra a Tabularis y cuánto paga; acá se decide qué se queda
 * publicado. Quien revisa una denuncia no debería estar mirando vencimientos en
 * la misma pantalla.
 *
 * Dos cosas que la pantalla tiene que dejar claras:
 *
 *   1. Ocultar NO es borrar. La publicación desaparece del foro y queda
 *      guardada, y se puede restaurar. Sin eso, moderar da miedo y nadie modera.
 *
 *   2. Acá se ve el nombre real aunque la publicación sea anónima. Es el único
 *      lugar del sistema donde eso pasa, y la pantalla lo dice, para que quien
 *      modera sepa que está viendo algo que el foro no muestra.
 */
import React, { useCallback, useEffect, useState } from 'react';

const C = { navy: '#0D1B35', blue: '#1A3A6E', blue2: '#2756A8', line: '#DDE6F3', muted: '#6B82A0' };

type Objeto = {
  tipo: 'tema' | 'respuesta';
  id: string; slug: string; titulo: string; texto: string;
  estado: 'VISIBLE' | 'OCULTO' | 'ELIMINADO';
  creado: string; autor: string; anonimo: boolean;
};
type Denuncia = {
  id: string; motivo: string; estado: 'PENDIENTE' | 'ACEPTADA' | 'DESESTIMADA';
  creado: string; nota: string | null; resueltoEn: string | null;
  denunciante: string; objeto: Objeto | null;
};

const fecha = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const ModuloForo: React.FC<{ api: <T,>(ruta: string, opciones?: any) => Promise<T> }> = ({ api }) => {
  const [denuncias, setDenuncias] = useState<Denuncia[] | null>(null);
  const [filtro, setFiltro] = useState<'PENDIENTE' | 'todas'>('PENDIENTE');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState('');
  const [notas, setNotas] = useState<Record<string, string>>({});

  const cargar = useCallback(() => {
    api<{ denuncias: Denuncia[] }>(`/plataforma/foro/denuncias?estado=${filtro}`)
      .then((r) => { setDenuncias(r.denuncias); setError(''); })
      .catch(() => setError('No pudimos cargar la cola de denuncias.'));
  }, [api, filtro]);
  useEffect(cargar, [cargar]);

  const resolver = async (d: Denuncia, accion: 'ocultar' | 'desestimar') => {
    setOcupado(d.id);
    try {
      await api(`/plataforma/foro/denuncias/${d.id}/resolver`, { method: 'POST', body: { accion, nota: notas[d.id] || undefined } });
      cargar();
    } catch (e: any) { setError(e?.message || 'No pudimos resolver la denuncia.'); }
    finally { setOcupado(''); }
  };

  const restaurar = async (o: Objeto) => {
    setOcupado(o.id);
    try {
      await api('/plataforma/foro/restaurar', {
        method: 'POST',
        body: o.tipo === 'tema' ? { temaId: o.id } : { respuestaId: o.id },
      });
      cargar();
    } catch { setError('No pudimos restaurar la publicación.'); }
    finally { setOcupado(''); }
  };

  const pendientes = (denuncias || []).filter((d) => d.estado === 'PENDIENTE').length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, margin: 0 }}>Moderación del foro</h1>
          <p style={{ color: C.muted, margin: '4px 0 0', fontSize: 14 }}>
            {denuncias === null ? 'Cargando…' : pendientes === 0 ? 'Nada pendiente por revisar' : `${pendientes} denuncia${pendientes === 1 ? '' : 's'} esperando`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['PENDIENTE', 'todas'] as const).map((f) => (
            <button
              key={f} onClick={() => setFiltro(f)}
              style={{
                fontSize: 13, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${filtro === f ? C.blue2 : C.line}`,
                background: filtro === f ? C.blue2 : '#fff', color: filtro === f ? '#fff' : C.muted,
              }}
            >{f === 'PENDIENTE' ? 'Pendientes' : 'Todas'}</button>
          ))}
        </div>
      </div>

      {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 14 }}>{error}</div>}

      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 14, lineHeight: 1.6, maxWidth: '72ch' }}>
        Ocultar retira la publicación del foro pero <strong style={{ color: C.navy }}>no la borra</strong>: queda guardada y se puede restaurar.
        Acá se ve el nombre real del autor aunque haya publicado en anónimo — es el único lugar donde eso ocurre.
      </div>

      {denuncias !== null && denuncias.length === 0 && (
        <div style={{ marginTop: 26, padding: '30px 22px', border: `1px solid ${C.line}`, borderRadius: 12, background: '#fff', color: C.muted, fontSize: 14 }}>
          {filtro === 'PENDIENTE' ? 'No hay denuncias pendientes. El foro está tranquilo.' : 'Todavía no se ha denunciado nada.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
        {(denuncias || []).map((d) => {
          const o = d.objeto;
          const resuelta = d.estado !== 'PENDIENTE';
          return (
            <div key={d.id} style={{ border: `1px solid ${C.line}`, borderRadius: 12, background: '#fff', padding: 18 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
                  {o ? (o.tipo === 'tema' ? 'Tema' : 'Respuesta') : 'Publicación borrada'}
                </span>
                {resuelta && (
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 4,
                    background: d.estado === 'ACEPTADA' ? '#FDEDED' : '#EEF4FA',
                    color: d.estado === 'ACEPTADA' ? '#8A2A2A' : C.blue2,
                  }}>{d.estado === 'ACEPTADA' ? 'Se ocultó' : 'Se desestimó'}</span>
                )}
                {o?.estado === 'OCULTO' && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#FDEDED', color: '#8A2A2A' }}>Oculta del foro</span>}
                <span style={{ fontSize: 12, color: C.muted, marginLeft: 'auto' }}>Denunciada por {d.denunciante} · {fecha(d.creado)}</span>
              </div>

              <div style={{ background: '#FFF8E8', border: '1px solid #F0E0B8', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#6A5320', marginBottom: 12 }}>
                <strong>Motivo:</strong> {d.motivo}
              </div>

              {o ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.navy, marginBottom: 4 }}>{o.titulo}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                    {o.autor}
                    {o.anonimo && <span style={{ color: '#8A5A00' }}> · publicó en anónimo</span>}
                    {' · '}{fecha(o.creado)}
                    {' · '}
                    <a href={`/publico/foro/t/${o.slug}`} target="_blank" rel="noreferrer" style={{ color: C.blue2 }}>ver en el foro ↗</a>
                  </div>
                  <div style={{
                    fontSize: 14, lineHeight: 1.7, color: '#33455F', whiteSpace: 'pre-wrap',
                    background: '#F7FAFD', border: `1px solid ${C.line}`, borderRadius: 8, padding: '12px 14px',
                    maxHeight: 220, overflowY: 'auto',
                  }}>{o.texto}</div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: C.muted }}>La publicación ya no existe: la borró su autor.</div>
              )}

              {!resuelta && o && (
                <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={notas[d.id] || ''}
                    onChange={(e) => setNotas((n) => ({ ...n, [d.id]: e.target.value }))}
                    placeholder="Nota para el registro (opcional)"
                    style={{ flex: 1, minWidth: 200, padding: '8px 11px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13 }}
                  />
                  <button
                    onClick={() => resolver(d, 'desestimar')} disabled={ocupado === d.id}
                    style={{ fontSize: 13, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${C.line}`, background: '#fff', color: C.muted }}
                  >Desestimar</button>
                  <button
                    onClick={() => resolver(d, 'ocultar')} disabled={ocupado === d.id}
                    style={{ fontSize: 13, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', border: '1px solid #E8C4C4', background: '#fff', color: '#A32A2A', fontWeight: 600 }}
                  >Ocultar del foro</button>
                </div>
              )}

              {d.nota && <div style={{ marginTop: 10, fontSize: 12.5, color: C.muted }}><strong>Nota:</strong> {d.nota}</div>}

              {resuelta && o?.estado === 'OCULTO' && (
                <button
                  onClick={() => restaurar(o)} disabled={ocupado === o.id}
                  style={{ marginTop: 12, fontSize: 13, padding: '7px 13px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${C.line}`, background: '#fff', color: C.blue2 }}
                >Volver a publicar</button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ModuloForo;
